/** Pure Reverse Girvi field mapping. No API client imports. */

function num(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function ymd(value) {
  if (!value) return "";
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  const date = new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function addToDate(startYmd, amount, unit) {
  const base = ymd(startYmd);
  if (!base) return "";
  const date = new Date(`${base}T00:00:00`);
  const count = parseInt(amount, 10) || 0;
  if (!count) return base;
  if (String(unit || "Month").toLowerCase().startsWith("day")) {
    date.setDate(date.getDate() + count);
  } else {
    const day = date.getDate();
    date.setMonth(date.getMonth() + count);
    if (date.getDate() < day) date.setDate(0);
  }
  return ymd(date);
}

export function suggestedMonthlyInterest(remaining, interestPercent) {
  return (num(remaining) * num(interestPercent)) / 100;
}

export function isReverseGirviClosed(record) {
  return String(record?.status || "").trim().toUpperCase() === "CLOSED";
}

export function reverseGirviStatusLabel(record) {
  if (isReverseGirviClosed(record)) return "Closed";
  const raw = String(record?.status || "").trim().toUpperCase();
  if (raw === "ACTIVE") return "Active";
  return num(record?.remaining_amount ?? record?.remaining_amt) === 0
    ? "Closed"
    : "Active";
}

/** Shop payout guard. Returns an error message, or "" when the payout is allowed. */
export function validateReversePayout({
  status,
  remaining,
  principalPaid,
  interestPaid,
}) {
  if (isReverseGirviClosed({ status })) {
    return "This deposit is closed";
  }
  const principal = num(principalPaid);
  const interest = num(interestPaid);
  if (principal + interest <= 0) {
    return "Enter interest or principal to pay the customer";
  }
  if (principal - num(remaining) > 0.009) {
    return "Principal paid cannot exceed the remaining amount";
  }
  return "";
}

export function aliasReverseDetail(detail) {
  if (!detail || typeof detail !== "object") return detail;
  const principal = num(
    detail.principal_taken ?? detail.principal_amount ?? detail.principal_given,
  );
  const date =
    detail.reverse_girvi_date ||
    detail.start_date ||
    detail.doc_date ||
    detail.girvi_date ||
    "";
  const number =
    detail.reverse_girvi_number ||
    detail.girvi_number ||
    detail.doc_number ||
    "";
  const remaining =
    detail.remaining_amount ?? detail.remaining_amt ?? principal;
  return {
    ...detail,
    principal_given: detail.principal_given ?? principal,
    principal_taken: detail.principal_taken ?? principal,
    principal_amount: detail.principal_amount ?? principal,
    girvi_date: detail.girvi_date || date,
    reverse_girvi_date: detail.reverse_girvi_date || date,
    start_date: detail.start_date || date,
    doc_date: detail.doc_date || date,
    girvi_number: number,
    reverse_girvi_number: detail.reverse_girvi_number || number,
    remaining_amount: remaining,
    remaining_amt: detail.remaining_amt ?? remaining,
  };
}

export function aliasReversePayments(payments) {
  if (!Array.isArray(payments)) return [];
  return payments.map((payment) => {
    const principalPaid = num(
      payment?.principal_paid ?? payment?.principal_received,
    );
    const interestPaid = num(
      payment?.interest_paid ?? payment?.interest_received,
    );
    const hasAmount =
      payment?.amount != null && payment.amount !== "" && payment.amount !== 0;
    const amount = hasAmount ? payment.amount : principalPaid + interestPaid;
    return {
      ...payment,
      principal_paid: principalPaid,
      interest_paid: interestPaid,
      amount,
    };
  });
}

export function aliasReverseShowItem(item) {
  if (!item || typeof item !== "object") return item;
  const rawDetail =
    item.reverse_girvi_details || item.girvi_details || item.detail || item;
  const detail = aliasReverseDetail(rawDetail);
  const payments = aliasReversePayments(item.payments || detail.payments || []);
  return {
    ...item,
    ...detail,
    girvi_details: detail,
    reverse_girvi_details: detail,
    detail,
    payments,
    items: [],
  };
}

export function aliasReverseShowResponse(response) {
  if (!response || typeof response !== "object") return response;
  const data = response.data;
  if (Array.isArray(data)) {
    return { ...response, data: data.map(aliasReverseShowItem) };
  }
  if (data && typeof data === "object") {
    return { ...response, data: aliasReverseShowItem(data) };
  }
  return response;
}

export function aliasReverseMutationResponse(response) {
  if (!response || typeof response !== "object" || !response.data) return response;
  const data = response.data;
  if (Array.isArray(data) || typeof data !== "object") return response;
  const documentId =
    data.reverse_girvi_document_id ?? data.girvi_document_id ?? null;
  return {
    ...response,
    data: {
      ...data,
      reverse_girvi_document_id: documentId,
      girvi_document_id: data.girvi_document_id ?? documentId,
    },
  };
}

function formValue(formData, key) {
  if (!(formData instanceof FormData) || !formData.has(key)) return "";
  const value = formData.get(key);
  return typeof value === "string" ? value : "";
}

/** Outgoing create/update body: reverse keys only. Drops Girvi receive-side fields. */
export function translateReverseGirviFormData(formData) {
  const next = new FormData();
  if (!(formData instanceof FormData)) return next;

  for (const [key, value] of formData.entries()) {
    if (
      key === "principal_given" ||
      key === "principal_received" ||
      key === "interest_received" ||
      key === "girvi_id" ||
      key === "items" ||
      key === "items_image" ||
      /^image_\d+$/.test(key)
    ) {
      continue;
    }
    if (key === "girvi_date" || key === "reverse_girvi_date") continue;
    next.append(key, value);
  }

  const principal =
    formValue(formData, "principal_amount") ||
    formValue(formData, "principal_given") ||
    formValue(formData, "principal_taken");
  if (principal && !next.has("principal_amount")) {
    next.append("principal_amount", principal);
  }

  const documentId =
    formValue(formData, "reverse_girvi_id") || formValue(formData, "girvi_id");
  if (documentId && !next.has("reverse_girvi_id")) {
    next.append("reverse_girvi_id", documentId);
  }

  const start =
    formValue(formData, "start_date") ||
    formValue(formData, "girvi_date") ||
    formValue(formData, "reverse_girvi_date");
  if (start && !next.has("start_date")) {
    next.append("start_date", start);
  }

  const period = formValue(formData, "return_period");
  const periodType = formValue(formData, "return_period_type") || "Month";
  if (period && !next.has("tenure_months")) {
    const months = String(periodType).toLowerCase().startsWith("day")
      ? String(Math.max(1, Math.round((parseInt(period, 10) || 0) / 30)))
      : String(parseInt(period, 10) || 0);
    if (months && months !== "0") next.append("tenure_months", months);
  }
  if (start && period && !next.has("due_date")) {
    const due = addToDate(start, period, periodType);
    if (due) next.append("due_date", due);
  }

  return next;
}

export function translateReverseSettlementPayload(payload = {}) {
  const source = payload.payment || {};
  const principalPaid = num(
    source.principal_paid ?? source.principal_received ?? payload.principal_paid,
  );
  const interestPaid = num(
    source.interest_paid ?? source.interest_received ?? payload.interest_paid,
  );
  const payment = {
    principal_paid: principalPaid,
    interest_paid: interestPaid,
    payment_date: source.payment_date || payload.payment_date || "",
    from_date: source.from_date || payload.from_date || null,
    to_date: source.to_date || payload.to_date || null,
    int_month:
      source.int_month != null
        ? source.int_month
        : payload.int_month != null
          ? payload.int_month
          : 0,
    interest_days:
      source.interest_days != null
        ? source.interest_days
        : payload.interest_days != null
          ? payload.interest_days
          : 0,
    pay_mode: source.pay_mode || payload.pay_mode || "",
    bank_type:
      source.bank_type ?? source.bank_id ?? payload.bank_type ?? payload.bank_id ?? 0,
    remarks: source.remarks ?? payload.remarks ?? "",
  };

  return {
    type: "settlement",
    idempotency_key: payload.idempotency_key,
    reverse_girvi_id:
      payload.reverse_girvi_id ?? payload.girvi_id ?? payload.id,
    payment,
  };
}

export function mapReverseGirviSocketDetail(data) {
  const root = data?.reverse_girvi || data?.girvi || null;
  if (!root) return null;
  const detail = aliasReverseDetail(
    root.detail || root.reverse_girvi_details || root.girvi_details || root,
  );
  if (!detail) return null;
  return {
    id: detail.id ?? detail.reverse_girvi_document_id ?? root.id,
    girvi_number: detail.girvi_number || "",
    reverse_girvi_number: detail.reverse_girvi_number || "",
    customer_name: detail.customer_name || "",
    customer_mobile: detail.customer_mobile || "",
    principal_given: num(detail.principal_taken),
    principal_taken: num(detail.principal_taken),
    remaining_amount: num(detail.remaining_amount),
    user_id: detail.user_id,
    girvi_date: detail.girvi_date || "",
    reverse_girvi_date: detail.reverse_girvi_date || "",
    due_date: detail.due_date || null,
    return_period: detail.return_period,
    return_period_type: detail.return_period_type,
    interest_percent: detail.interest_percent,
    monthly_interest: detail.monthly_interest,
    tenure_months: detail.tenure_months,
    status: detail.status,
    created_at: detail.created_at,
  };
}
